import React, { useCallback, useContext } from "react";
import {
  FEATURED_ITEM_ENTITY_TYPE,
  FEATURED_ITEM_LINK_TYPE,
} from "common/constants";
import { RouteNames } from "common/routing";
import { buildFullPublicWebsiteURL } from "common/utils/url";
import { useConfirmDestructiveAction } from "../../hooks/useConfirmDestructiveAction";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { ButtonLink } from "../Common/ButtonLink";
import { Body } from "../Common/Typography";
import { ActionOptions, ActionsProps } from "../Inputs/DropdownMenu";
import DropdownMenu from "../Inputs/DropdownMenu";
import type { FeaturedItemWithImage } from "../Settings/Account/FeaturedContent/featuredContentInputReducer";
import type { FeaturedItemInput } from "../Settings/Account/FeaturedContent/FeaturedContentSectionsInput";
import { useEditFeaturedItemModal } from "../Settings/Account/FeaturedContent/FeaturedItemModal";
import { getFeaturedItemId } from "../Settings/Account/FeaturedContent/utils";
import {
  ActionMenuWrapper,
  Container,
  Image,
  TextContent,
} from "./__styles__/FeaturedItem";

type FeaturedItemImage = {
  url: string;
  accessibilityText: string;
};

type FeaturedItemLink = Omit<ActionOptions, "type"> & {
  text: string;
  type?: Maybe<FEATURED_ITEM_LINK_TYPE>;
};

const assertNever = (value: never): never => {
  throw new Error(
    `Unhandled entity type: ${value}. Add a case to handle this entity type.`,
  );
};

const getFeaturedItemLinkProps = (
  entityType: FEATURED_ITEM_ENTITY_TYPE,
  entityId: string,
  link: FeaturedItemLink,
  subdomain: string,
): ActionOptions => {
  const getLink = (
    routeName: RouteNames,
    params: Record<string, string> = {},
    queryParams: Record<
      string,
      Maybe<string> | Maybe<string[]> | undefined
    > = {},
  ): string => {
    return buildFullPublicWebsiteURL({
      subdomain,
      routeName,
      params,
      queryParams,
    });
  };

  switch (entityType) {
    case FEATURED_ITEM_ENTITY_TYPE.WORKFLOW_DEFINITION:
      return {
        href: getLink("workflowDefinition", {
          workflowDefinitionId: entityId,
        }),
        target: "_blank",
      };
    case FEATURED_ITEM_ENTITY_TYPE.SUBMISSION_TYPE:
      return {
        href: getLink("createSubmission", {
          submissionTypeId: entityId,
        }),
        target: "_blank",
      };
    case FEATURED_ITEM_ENTITY_TYPE.SAVED_VIEW:
      if (link.type === FEATURED_ITEM_LINK_TYPE.MAP_LAYER) {
        return {
          href: getLink("explore", {}, { savedViewId: entityId }),
          target: "_blank",
        };
      }
      return {
        href: getLink("residentSavedView", {
          savedViewId: entityId,
        }),
        target: "_blank",
      };
    default:
      return assertNever(entityType);
  }
};

type FeaturedItemEditProps = {
  item: FeaturedItemWithImage;
  sectionId: string;
  onUpdate: (
    sectionId: string,
    itemId: string,
    data: FeaturedItemInput,
  ) => void;
  onDelete: (sectionId: string, itemId: string) => void;
};

export type FeaturedItemProps = {
  id?: string;
  image: FeaturedItemImage;
  title: string;
  description: string;
  link: FeaturedItemLink;
  entityType: FEATURED_ITEM_ENTITY_TYPE;
  entityId: string;
  actions?: Array<ActionsProps>;
  editProps?: FeaturedItemEditProps;
};

const FeaturedItemContent = ({
  image,
  title,
  description,
  link,
  linkProps,
  actions,
  onButtonClick,
}: {
  image: FeaturedItemImage;
  title: string;
  description: string;
  link: FeaturedItemLink;
  linkProps: ActionOptions;
  actions?: Array<ActionsProps>;
  onButtonClick?: () => void;
}) => {
  return (
    <Container>
      {actions && actions.length > 0 && (
        <ActionMenuWrapper
          onPointerDown={e => {
            e.stopPropagation();
          }}
          onMouseDown={e => {
            e.stopPropagation();
          }}
        >
          <DropdownMenu actions={actions} />
        </ActionMenuWrapper>
      )}
      <Image src={image.url} alt={image.accessibilityText} />
      <TextContent>
        <Body size="small" type="emphasis" style={{ marginBottom: 0 }}>
          {title}
        </Body>
        <Body
          size="small"
          type="regular"
          color="contentSecondary"
          style={{ marginBottom: 0 }}
        >
          {description}
        </Body>
      </TextContent>
      {link && (
        <span onClick={onButtonClick}>
          <ButtonLink
            styleVariant="primary"
            size="small"
            rightIconName="arrow-right"
            {...linkProps}
          >
            {link.text}
          </ButtonLink>
        </span>
      )}
    </Container>
  );
};

const FeaturedItemWithEdit = ({
  image,
  title,
  description,
  link,
  linkProps,
  editProps,
}: {
  image: FeaturedItemImage;
  title: string;
  description: string;
  link: FeaturedItemLink;
  linkProps: ActionOptions;
  editProps: FeaturedItemEditProps;
}) => {
  const handleUpdate = useCallback(
    (data: FeaturedItemInput) => {
      const itemId = getFeaturedItemId(editProps.item);
      editProps.onUpdate(editProps.sectionId, itemId, data);
    },
    [editProps],
  );

  const [showEditModal] = useEditFeaturedItemModal({
    item: editProps.item,
    onUpdate: handleUpdate,
  });

  const showDeleteConfirmation = useConfirmDestructiveAction({
    title: "Delete featured content",
    subtitle: editProps.item.title,
    content: (
      <Body size="large" type="regular">
        This featured content will be removed.{" "}
        <Body size="large" type="emphasis" as="span">
          This action cannot be undone.
        </Body>
      </Body>
    ),
    onConfirm: hideModal => {
      const itemId = getFeaturedItemId(editProps.item);
      editProps.onDelete(editProps.sectionId, itemId);
      hideModal();
    },
  });

  const editActions: Array<ActionsProps> = [
    {
      label: "Edit",
      onClick: () => {
        showEditModal();
      },
    },
    {
      label: "Delete content",
      variant: "red",
      onClick: () => {
        showDeleteConfirmation();
      },
    },
  ];

  return (
    <FeaturedItemContent
      image={image}
      title={title}
      description={description}
      link={link}
      linkProps={linkProps}
      actions={editActions}
    />
  );
};

export const FeaturedItem = ({
  id,
  image,
  title,
  description,
  link,
  entityType,
  entityId,
  actions,
  editProps,
}: FeaturedItemProps) => {
  const { account, user } = useContext(AuthContext);
  if (!account?.subdomain) {
    return null;
  }
  const linkProps = getFeaturedItemLinkProps(
    entityType,
    entityId,
    link,
    account.subdomain,
  );

  if (editProps) {
    return (
      <FeaturedItemWithEdit
        image={image}
        title={title}
        description={description}
        link={link}
        linkProps={linkProps}
        editProps={editProps}
      />
    );
  }

  const handleButtonClick = () => {
    track("Featured Item Clicked", {
      accountId: account.id,
      userId: user?.id,
      featuredItemId: id,
      entityType,
      title,
    });
  };

  const displayActions = actions;

  return (
    <FeaturedItemContent
      image={image}
      title={title}
      description={description}
      link={link}
      linkProps={linkProps}
      actions={displayActions}
      onButtonClick={handleButtonClick}
    />
  );
};
